import { usePluginByName } from '@/api/hooks/plugins';
import { resolveCrumbLabel } from '@/components/breadcrumbs/resolveCrumbLabel';
import { pluginTitle } from '@/lib/utils';
import type { ReactElement } from 'react';

/**
 * Breadcrumb leaf for `/catalog/:slug`. Resolves the plugin's display title
 * from the SAME `usePluginByName(slug)` cache entry the detail page
 * populates — React Query dedupes by query key, so this costs zero extra
 * network. Falls back to the raw slug while the fetch is in flight, if it
 * failed, or if the slug doesn't resolve — a failed fetch gets a `title`
 * tooltip so it isn't visually indistinguishable from "still loading" (the
 * actual error is already telemetered by the mutator; this is just an
 * operator-facing hint).
 *
 * Previously read a page of `useListPlugins()` and `.find()`-ed the slug, so
 * it degraded to the raw slug for every plugin past the 25-row default page.
 * See ADR 0009.
 */
export function PluginCrumb({ slug }: { slug: string | undefined }): ReactElement {
  const { data, isError } = usePluginByName(slug);
  const label = resolveCrumbLabel(data, slug, pluginTitle, 'Plugin');
  return isError ? <span title="Couldn't load the plugin name">{label}</span> : <>{label}</>;
}
